import { z } from 'zod';

import type { SafeStatus } from './equipmentLifecycle';

export const EstoqueLifecycleStatusSchema = z.enum([
  'EM_ESTOQUE',
  'ALOCADO',
  'EM_MANUTENCAO',
  'EM_TRANSITO',
  'SUSPENSO',
  'EM_DEVOLUCAO',
  'DESATIVADO',
]);

export type EstoqueLifecycleStatus = z.infer<typeof EstoqueLifecycleStatusSchema>;

export const EstoqueJoinStatusSchema = z.enum(['MATCHED', 'M2_ONLY', 'ICLASS_ONLY']);

export type EstoqueJoinStatus = z.infer<typeof EstoqueJoinStatusSchema>;

export const EstoqueOsAbertaSchema = z.object({
  osId: z.string(),
  display: z.string(),
  severity: z.enum(['none', 'warn', 'critical']),
});

export type EstoqueOsAberta = z.infer<typeof EstoqueOsAbertaSchema>;

export const EstoqueKpisSchema = z.object({
  emEstoque: z.number().int().nonnegative(),
  alocados: z.number().int().nonnegative(),
  emManutencao: z.number().int().nonnegative(),
  emTransito: z.number().int().nonnegative(),
  pecasAtivas: z.number().int().nonnegative(),
  pecasCategorias: z.number().int().nonnegative(),
  pecasEstoqueBaixo: z.number().int().nonnegative(),
});

export type EstoqueKpis = z.infer<typeof EstoqueKpisSchema>;

export const EstoqueEquipmentRowSchema = z.object({
  serial: z.string(),
  iclassAtivoId: z.number().nullable(),
  tipoEquipamento: z.string(),
  statusLifecycle: EstoqueLifecycleStatusSchema,
  statusLabel: z.string(),
  statusChip: z.string(),
  empresaFilial: z.string().nullable(),
  designador: z.string().nullable(),
  osAberta: EstoqueOsAbertaSchema.nullable(),
  firmware: z.string().nullable(),
  instaladoEm: z.string().nullable(),
  localizacao: z.string().nullable(),
  joinStatus: EstoqueJoinStatusSchema,
  podeAlocar: z.boolean(),
});

export type EstoqueEquipmentRow = z.infer<typeof EstoqueEquipmentRowSchema>;

export const EstoqueMaterialRowSchema = z.object({
  materialId: z.string(),
  nome: z.string(),
  codigo: z.string(),
  compativelCom: z.string().nullable(),
  quantidade: z.number(),
  estoqueMinimo: z.number().nullable(),
  status: z.enum(['OK', 'BAIXO']),
  statusChip: z.string(),
  local: z.string(),
});

export type EstoqueMaterialRow = z.infer<typeof EstoqueMaterialRowSchema>;

export const EstoqueMetaSchema = z.object({
  generatedAt: z.string().datetime(),
  joinStats: z.object({
    matched: z.number().int().nonnegative(),
    m2Only: z.number().int().nonnegative(),
    iclassOnly: z.number().int().nonnegative(),
  }),
  provider: z.enum(['mock', 'iclass-v2']),
  degraded: z.boolean().optional(),
});

export type EstoqueMeta = z.infer<typeof EstoqueMetaSchema>;

export const EstoqueResponseSchema = z.object({
  kpis: EstoqueKpisSchema,
  equipamentos: z.array(EstoqueEquipmentRowSchema),
  pecas: z.array(EstoqueMaterialRowSchema),
  meta: EstoqueMetaSchema,
});

export type EstoqueResponse = z.infer<typeof EstoqueResponseSchema>;

const M2_SNAKE_TO_ESTOQUE: Record<string, EstoqueLifecycleStatus> = {
  em_estoque: 'EM_ESTOQUE',
  alocado: 'ALOCADO',
  em_manutencao: 'EM_MANUTENCAO',
  em_transito: 'EM_TRANSITO',
  desativado: 'DESATIVADO',
  suspenso: 'SUSPENSO',
  em_devolucao: 'EM_DEVOLUCAO',
};

const SAFE_TO_ESTOQUE: Record<SafeStatus, EstoqueLifecycleStatus> = {
  EM_ESTOQUE: 'EM_ESTOQUE',
  ALOCADO: 'ALOCADO',
  SUSPENSO: 'SUSPENSO',
  EM_DEVOLUCAO: 'EM_DEVOLUCAO',
  DESATIVADO: 'DESATIVADO',
};

export function mapM2StatusToEstoqueLifecycle(m2Status: string): EstoqueLifecycleStatus {
  return M2_SNAKE_TO_ESTOQUE[m2Status] ?? 'EM_ESTOQUE';
}

export function mapLifecycleToUi(status: EstoqueLifecycleStatus): { label: string; chip: string } {
  const table: Record<EstoqueLifecycleStatus, { label: string; chip: string }> = {
    EM_ESTOQUE: { label: 'Em Estoque', chip: 'chip-blue' },
    ALOCADO: { label: 'Alocado', chip: 'chip-green' },
    EM_MANUTENCAO: { label: 'Em Manutenção', chip: 'chip-orange' },
    EM_TRANSITO: { label: 'Em Trânsito', chip: 'chip-blue' },
    SUSPENSO: { label: '⛔ Suspenso', chip: 'chip-orange' },
    EM_DEVOLUCAO: { label: 'Em Devolução', chip: 'chip-gray' },
    DESATIVADO: { label: 'Desativado', chip: 'chip-gray' },
  };
  return table[status];
}

export function mapSafeStatusToEstoqueLabel(status: SafeStatus): { label: string; chip: string } {
  return mapLifecycleToUi(SAFE_TO_ESTOQUE[status]);
}
